import React, { Suspense, lazy, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/toaster';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import Layout from './components/Layout';
// CookieBanner kaldırıldı - Türkiye için Analytics onay gerektirmez
import ErrorBoundary from './components/ErrorBoundary';

// Eager load critical pages (shown immediately)
import HomePage from './pages/HomePage';
import VenuesPage from './pages/VenuesPage';
import SlugRouter from './pages/SlugRouter';
import VenueDetailPage from './pages/VenueDetailPage';
import DistrictVenuePage from './pages/DistrictVenuePage';

// Lazy load other pages (code splitting)
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const RegisterPageNew = lazy(() => import('./pages/RegisterPageNew'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const VenueOwnerDashboard = lazy(() => import('./pages/VenueOwnerDashboardNew'));
const AddVenuePageNew = lazy(() => import('./pages/AddVenuePageNew'));
const AdminPanelPage = lazy(() => import('./pages/AdminPanelPage'));
const AdminPanelPageNew = lazy(() => import('./pages/AdminPanelPageNew'));
const AdminSMSPanel = lazy(() => import('./pages/AdminSMSPanel'));
const AdminUsersPanel = lazy(() => import('./pages/AdminUsersPanel'));
const AdminShopPanel = lazy(() => import('./pages/AdminShopPanel'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const CustomerLoginPage = lazy(() => import('./pages/CustomerLoginPage'));
// TestApiPage removed - not needed in production
// const VenueEntryPage = lazy(() => import('./pages/VenueEntryPage')); // Deprecated - using VenueApplicationPage instead
const VenueApplicationPage = lazy(() => import('./pages/VenueApplicationPage'));
const VenueOwnersLandingPage = lazy(() => import('./pages/VenueOwnersLandingPage'));
const VenueLoginPage = lazy(() => import('./pages/VenueLoginPage'));
const VitrinPage = lazy(() => import('./pages/VitrinPage'));
const AdTestPage = lazy(() => import('./pages/AdTestPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const KVKKPage = lazy(() => import('./pages/KVKKPage'));
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const DijitalNedimePage = lazy(() => import('./pages/DijitalNedimePage'));
const VotingPage = lazy(() => import('./pages/VotingPage'));
const ClaimLandingPage = lazy(() => import('./pages/ClaimLandingPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentFailPage = lazy(() => import('./pages/PaymentFailPage'));
const CityAppointmentPage = lazy(() => import('./pages/CityAppointmentPage'));
const NisanMaliyetHesaplayici = lazy(() => import('./pages/NisanMaliyetHesaplayici'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const ShopProductPage = lazy(() => import('./pages/ShopProductPage'));
const ShopCartPage = lazy(() => import('./pages/ShopCartPage'));
const ShopCheckoutPage = lazy(() => import('./pages/ShopCheckoutPage'));
const ShopOrderStatusPage = lazy(() => import('./pages/ShopOrderStatusPage'));
const ShopOrdersPage = lazy(() => import('./pages/ShopOrdersPage'));
const ShopLayout = lazy(() => import('./components/ShopLayout'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-pink-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Yükleniyor...</p>
    </div>
  </div>
);

// Scroll to top on route change
function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    // Don't scroll to top if we have a saved scroll position
    const hasSavedScroll = sessionStorage.getItem('venuesPageScroll');
    
    if (!location.state?.scrollPosition && !hasSavedScroll) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return null;
}

// Load AdSense Auto Ads
function AdSenseAutoAds() {
  useEffect(() => {
    const loadAutoAds = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${BACKEND_URL}/api/adsense-settings`);
        const data = await response.json();
        
        if (data.success && data.data.adsense_auto_enabled && data.data.adsense_auto_code) {
          const autoCode = data.data.adsense_auto_code;
          
          // Parse the script tag
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = autoCode;
          
          const scriptElement = tempDiv.querySelector('script[src*="adsbygoogle"]');
          
          if (scriptElement && !document.querySelector('script[src*="adsbygoogle"][data-auto-ads="true"]')) {
            const script = document.createElement('script');
            script.src = scriptElement.src;
            script.async = true;
            script.crossOrigin = 'anonymous';
            script.setAttribute('data-auto-ads', 'true');
            
            // Add to head
            document.head.appendChild(script);
            
            console.log('✅ AdSense Auto Ads yüklendi');
          }
        }
      } catch (error) {
        console.error('AdSense Auto Ads yükleme hatası:', error);
      }
    };
    
    loadAutoAds();
  }, []);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
        <HelmetProvider>
          <BrowserRouter>
              <ScrollToTop />
              <AdSenseAutoAds />
              <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/giris" element={<LoginPage />} />
              <Route path="/login" element={<LoginPage />} /> {/* Redirect for old links */}
              <Route path="/kayit" element={<AddVenuePageNew />} />
              <Route path="/register" element={<AddVenuePageNew />} /> {/* Redirect for old links */}
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/* <Route path="/venue-entry" element={<VenueEntryPage />} /> */}
              <Route path="/venue-entry" element={<VenueApplicationPage />} /> {/* Redirect to new page */}
              <Route path="/mekan-basvuru" element={<VenueApplicationPage />} />
              <Route path="/davet-evi-sahipleri" element={<VenueOwnersLandingPage />} />
              <Route path="/mekan-giris" element={<VenueLoginPage />} />
              <Route path="/vitrin" element={<VitrinPage />} />
              <Route path="/vitrin/:videoId" element={<VitrinPage />} />
              <Route path="/ad-test" element={<AdTestPage />} />
              <Route path="/dashboard" element={<VenueOwnerDashboard />} />
              <Route path="/dashboard-old" element={<DashboardPage />} />
              <Route path="/add-venue" element={<AddVenuePageNew />} />
              
              {/* Claim Landing Page */}
              <Route path="/isortagi-basvuru/:venueSlug" element={<ClaimLandingPage />} />
              
              {/* Legal Pages */}
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/kvkk" element={<KVKKPage />} />
              
              {/* Blog Pages */}
              <Route path="/blog" element={<Layout><BlogListPage /></Layout>} />
              <Route path="/blog/:slug" element={<Layout><BlogDetailPage /></Layout>} />
              
              {/* Dijital Nedime - Wedding Planner */}
              <Route path="/dijital-nedime" element={<Layout><DijitalNedimePage /></Layout>} />
              
              {/* Voting Page - Public */}
              <Route path="/oylama/:sessionId" element={<Layout><VotingPage /></Layout>} />
              
              <Route path="/admin" element={<AdminPanelPageNew />} />
              <Route path="/admin/sms" element={<AdminSMSPanel />} />
              <Route path="/admin/users" element={<AdminUsersPanel />} />
              <Route path="/admin/shop" element={<AdminShopPanel />} />
              
              {/* Customer Routes */}
              <Route path="/customer-login" element={<CustomerLoginPage />} />
              <Route path="/uye-giris" element={<CustomerLoginPage />} />
              <Route path="/hesabim" element={<CustomerDashboard />} />
              <Route path="/my-account" element={<CustomerDashboard />} />
              
              {/* Payment Result Pages */}
              <Route path="/odeme-basarili" element={<PaymentSuccessPage />} />
              <Route path="/odeme-basarisiz" element={<PaymentFailPage />} />
              
              {/* Appointment Pages */}
              <Route path="/randevu/nevsehir" element={<CityAppointmentPage cityKey="nevsehir" />} />
              <Route path="/randevu/konya" element={<CityAppointmentPage cityKey="konya" />} />
              
              <Route path="/admin-old" element={<AdminPanelPage />} />
              {/* TestApiPage route removed - not needed in production */}
              
              <Route path="/" element={<Layout><HomePage /></Layout>} />
              <Route path="/davet-evleri" element={<Layout><VenuesPage /></Layout>} />
              <Route path="/venues" element={<Layout><VenuesPage /></Layout>} /> {/* Redirect for old links */}

              {/* Nişan Maliyet Hesaplayıcı - Engagement Cost Calculator (SEO landing) */}
              <Route path="/nisan-maliyeti-hesaplama" element={<Layout><NisanMaliyetHesaplayici /></Layout>} />
              <Route path="/nisan-maliyet-hesaplayici" element={<Layout><NisanMaliyetHesaplayici /></Layout>} />

              {/* Nişan Hediyelikleri Shop — uses own ShopLayout (no main site header) */}
              <Route path="/nisan-hediyelikleri" element={<ShopLayout><ShopPage /></ShopLayout>} />
              <Route path="/nisan-hediyelikleri/urun/:slug" element={<ShopLayout><ShopProductPage /></ShopLayout>} />
              <Route path="/sepet" element={<ShopLayout><ShopCartPage /></ShopLayout>} />
              <Route path="/siparis" element={<ShopLayout><ShopCheckoutPage /></ShopLayout>} />
              <Route path="/nisan-hediyelikleri/siparis-basarili/:orderNumber" element={<ShopLayout><ShopOrderStatusPage success={true} /></ShopLayout>} />
              <Route path="/nisan-hediyelikleri/siparis-basarisiz/:orderNumber" element={<ShopLayout><ShopOrderStatusPage success={false} /></ShopLayout>} />
              <Route path="/nisan-hediyelikleri/siparislerim" element={<ShopLayout><ShopOrdersPage /></ShopLayout>} />
              
              {/* District Page with /davet-evi suffix */}
              <Route path="/:city/:district/davet-evi" element={<Layout><DistrictVenuePage /></Layout>} />
              
              {/* City/District/Venue Router - Handles 3-level URL structure */}
              <Route path="/:city/:district/:venueSlug" element={<Layout><VenueDetailPage /></Layout>} />
              
              {/* City/District Router - Handles 2-level URL structure */}
              <Route path="/:city/:district" element={<Layout><DistrictVenuePage /></Layout>} />
              
              {/* Slug Router - Handles venue details, district pages AND city pages */}
              <Route path="/:slug" element={<Layout><SlugRouter /></Layout>} />
              
              {/* 404 Not Found - Must be last */}
              <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
            </Routes>
          </Suspense>
          <Toaster />
          </BrowserRouter>
        </HelmetProvider>
        </CartProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
